import React from 'react';
import { useLocation } from 'react-router';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import config from 'src/constants';
import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { BarionPixel } from './BarionPixel/BarionPixel';
import { ICookieConsentProps } from '../CookieConsentFooter/CookieConsentFooter';
import { ITrackingCodes } from 'src/entities/company';

interface ITrackingProps extends ICookieConsentProps {
    hasConsent: boolean;
    gaClientId: string | null;
    trackingCodes: ITrackingCodes | null;
}

export const Tracking: React.FunctionComponent<ITrackingProps> = ({ barionPixelIds, hasConsent, gaClientId, trackingCodes }) => {
    const location = useLocation();
    const [isGaInitialized, setisGaInitialized] = useState(false);
    const [isGtagInitialized, setisGtagInitialized] = useState(false);

    const deniedConsent = {
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'ad_storage': 'denied',
        'analytics_storage': 'denied',
        'wait_for_update': 500,
    };

    const grantedConsent = {
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'ad_storage': 'granted',
        'analytics_storage': 'granted'
    }

    useEffect(() => {
        if (config.GA_TRACKING_ID) {
            setGADefaults();

            ReactGA.initialize([{ trackingId: config.GA_TRACKING_ID }]);
            ReactGA.send({ hitType: 'pageview', page: createGAPath() });
        }
    }, [])

    useEffect(() => {
        if (!trackingCodes) {
            return;
        }

        if (!isGtagInitialized && trackingCodes.GTMTrackingId) {
            setGtagDefaults();
            setisGtagInitialized(true);
        }

        if (!hasConsent) {
            return;
        }

        if (trackingCodes.FacebookPixel) {
            ReactPixel.init(trackingCodes.FacebookPixel);
            ReactPixel.pageView();
        }

        if (trackingCodes.GATrackingId) {
            const timeoutValue = trackingCodes.GASourceDomain && !gaClientId ? 3000 : 0;
            setTimeout(() => {
                createCustomTracker();
            }, timeoutValue);
        }
        else if (config.GA_TRACKING_ID) {
            setisGaInitialized(true);
        }
    }, [trackingCodes, hasConsent])

    useEffect(() => {
        if (isGtagInitialized && hasConsent) {
            updateGtagConsent();
        }
    }, [isGtagInitialized, hasConsent])

    useEffect(() => {
        if (isGaInitialized && hasConsent) {
            updateGAConsent();
        }
    }, [isGaInitialized, hasConsent])

    useEffect(() => {
        if (isGaInitialized) {
            ReactGA.send({ hitType: 'pageview', page: createGAPath() });
        }
    }, [location.pathname, location.search, location.hash])

    function createGAPath() {
        return location.pathname + location.hash + location.search;
    }

    function createCustomTracker() {
        if (!trackingCodes || !trackingCodes.GATrackingId) {
            setisGaInitialized(true);
            return;
        }

        const customTracker =
        {
            gaOptions: { allowLinker: true, gaClientId, name: 'customTracker' }
        };
        ReactGA.initialize(trackingCodes.GATrackingId, customTracker);
        ReactGA.send({ hitType: 'pageview', page: createGAPath(), send_to: trackingCodes.GATrackingId });

        setisGaInitialized(true);
    }

    function createDomainVerificationTag() {
        return trackingCodes?.FacebookDomainVerificationId
            ? <Helmet><meta name='facebook-domain-verification' content={trackingCodes.FacebookDomainVerificationId} /></Helmet>
            : null
    }

    function createBarionPixelTags() {
        return !!barionPixelIds?.length
            ? barionPixelIds.map((barionPixelId, key) => createBarionPixelTag(barionPixelId, key))
            : null
    }

    function createBarionPixelTag(barionPixelId: string, key: number) {
        return <BarionPixel key={key} barionPixelId={barionPixelId} />
    }

    function hasGtmTrackingId() {
        return trackingCodes?.GTMTrackingId;
    }

    function setGADefaults() {
        ReactGA.gtag('consent', 'default', deniedConsent);
    }

    function setGtagDefaults() {
        window.gtmDataLayer = window.gtmDataLayer || [];
        // eslint-disable-next-line prefer-rest-params
        window.gtm = function () { window.gtmDataLayer.push(arguments); }
        window.gtm('consent', 'default', deniedConsent);
        window.gtm('js', new Date());
        window.gtm('config', trackingCodes?.GTMTrackingId);

        const gtmScript = document.createElement('script');
        gtmScript.async = true;
        gtmScript.src = `https://www.googletagmanager.com/gtm/js?id=${trackingCodes?.GTMTrackingId}&l=gtmDataLayer`;

        document.head.appendChild(gtmScript);
    }

    function updateGtagConsent() {
        if (!hasGtmTrackingId()) {
            return;
        }

        window.gtm('consent', 'update', grantedConsent);
    }

    function updateGAConsent() {
        ReactGA.gtag('consent', 'update', grantedConsent);
    }

    return <>
        {createDomainVerificationTag()}
        {createBarionPixelTags()}
    </>;
}